import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { Routes} from '@datadog/browser-rum-react/react-router-v6';
import './App.css';
import Dashboard from './pages/dashboard';
import ErrorPage from './pages/error';
import OperationsPrintView from './pages/operations/OperationsPrintView';
import PerformancePrintView from './pages/performance/PerformancePrintView';
import ReactGA from 'react-ga4';
import { datadogRum } from '@datadog/browser-rum';
import { env } from './env';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ErrorNotification from './components/errornotification';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import {
  setMockUserData,
  selectAuthError,
  selectUserDetails,
  selectUserLoading,
  selectSuppressGenericErrorMessage,
  setTokenExpiredError,
  fetchUserDetailsFromSession,
} from './redux/UserReducer/userSlice';
import { setError } from './redux/ErrorReducer/errorSlice';
import { MOCK_USER_DATA } from './models/UserModel';
import translations from './languages/en.json';
import '@ace/ace-ui-web/dist/styles/styles.scss';
import LoadingOverlay from './components/loadingoverlay';

// Token expiration constants
const TOKEN_EXPIRATION_WARNING_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
const TOKEN_CHECK_INTERVAL_MS = 60000; // 1 minute

ReactGA.initialize(`${env.REACT_APP_GA_MEASUREMENT_ID}`, {
  testMode: env.REACT_APP_GA_TEST_MODE,
});

function App() {
  const dispatch = useAppDispatch();
  const authError = useAppSelector(selectAuthError);
  const suppressGenericErrorMessage = useAppSelector(selectSuppressGenericErrorMessage);
  const userDetails = useAppSelector(selectUserDetails);
  const isUserInformationLoading = useAppSelector(selectUserLoading);
  const warningShownRef = React.useRef(false);

  // Fetch user details from session on mount
  useEffect(() => {
    // Set mock user data for local development
    const isLocalDevelopment = window.location.hostname === 'localhost';
    if (isLocalDevelopment) {
      dispatch(setMockUserData(MOCK_USER_DATA));
    } else {
      dispatch(fetchUserDetailsFromSession());
    }
  }, [dispatch]);

  // Tag Datadog RUM events with the current user once details are loaded.
  useEffect(() => {
    if (!userDetails) return;
    datadogRum.setUser({
      id: userDetails.userId,
      name: userDetails.userId,
      storeNumber: userDetails.currentStoreNumber,
      userType: userDetails.userType,
    });
  }, [userDetails]);

  // Check token expiration every minute
  useEffect(() => {
    const checkTokenExpiration = () => {
      if (userDetails?.ibTokenExpiration) {
        // API will return the token expiration as UTC time via string
        const expirationDate = new Date(userDetails.ibTokenExpiration);
        const now = new Date();
        // 15 minutes before the expiration time we need to warn the user
        const timeUntilExpiration = expirationDate.getTime() - now.getTime();

        // Check if token has expired. If it has expired the user will be redirected to a new screen prompting
        // them to return to ACENET/ARMA
        if (now >= expirationDate) {
          dispatch(setTokenExpiredError());
        }
        // Check if token expires in 15 minutes or less (but not expired yet)
        // If so, and we haven't shown the warning yet, show it
        else if (
          timeUntilExpiration <= TOKEN_EXPIRATION_WARNING_THRESHOLD_MS &&
          timeUntilExpiration > 0 &&
          !warningShownRef.current
        ) {
          // Format the expiration time for display in MM/DD/YYYY h:mm AM/PM format
          const expirationTimeString = expirationDate.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });

          const errorMessage = translations.commonErrors.tokenExpirationWarning.replace(
            '${expirationTimeString}',
            expirationTimeString,
          );
          // Open popup with warning message
          dispatch(
            setError({
              data: errorMessage,
              status: 403.1,
            }),
          );
          warningShownRef.current = true;
        }
      }
    };

    // Check immediately to make sure the token is not expired
    checkTokenExpiration();

    // Check the token expiration every minute
    const intervalId = setInterval(checkTokenExpiration, TOKEN_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [userDetails, dispatch]);

  // Show auth error screen if error exists
  if (authError) {
    return <ErrorPage message={authError} hideGenericMessage={suppressGenericErrorMessage} />;
  }

  return (
    <ErrorBoundary>
      <LoadingOverlay isLoading={isUserInformationLoading} />
      <Router basename="/">
        <ErrorNotification />
        <Routes>
          <Route
            path="/"
            element={<Dashboard />}
          />
          <Route
            path="/print-operations"
            element={<OperationsPrintView />}
          />
          <Route
            path="/print-performance"
            element={<PerformancePrintView />}
          />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
